import React, { useEffect, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { api } from "../services/api";
import PageHeader from "./ui/PageHeader";
import DataCard from "./ui/DataCard";
import StatusBadge from "./ui/StatusBadge";
import LoadingState from "./ui/LoadingState";
import AlertBanner from "./ui/AlertBanner";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      setTransactions(await api.getTransactions());
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const filteredTxns = transactions.filter((txn) => {
    const matchesSearch =
      txn.type.toLowerCase().includes(filter.toLowerCase()) ||
      txn.status.toLowerCase().includes(filter.toLowerCase()) ||
      txn.user.toLowerCase().includes(filter.toLowerCase()) ||
      txn.id.toLowerCase().includes(filter.toLowerCase());
    const inDateRange =
      (!dateFrom || txn.date >= dateFrom) && (!dateTo || txn.date <= dateTo);
    return matchesSearch && inDateRange;
  });

  const runAction = async (txnRef, action) => {
    setActionLoading(`${txnRef}-${action}`);
    setFeedback(null);
    try {
      const result = await api.transactionAction(txnRef, action);
      setTransactions((prev) =>
        prev.map((txn) =>
          txn.id === txnRef
            ? { ...txn, status: result.status, statusKey: result.statusKey, flagged: result.flagged, failureReason: result.failureReason }
            : txn
        )
      );
      setFeedback({ type: "success", message: `${action} applied to ${txnRef}` });
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <LoadingState message="Loading transactions..." />;

  return (
    <div>
      <PageHeader title="Transactions" subtitle="Monitor and manage all bill payment transactions" />

      {feedback && (
        <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />
      )}

      <Row className="g-3 mb-4">
        <Col md={4}>
          <input className="billr-input" placeholder="Search ID, user, type..." value={filter} onChange={(e) => setFilter(e.target.value)} />
        </Col>
        <Col md={3}>
          <input className="billr-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </Col>
        <Col md={3}>
          <input className="billr-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </Col>
      </Row>

      <DataCard title={`All Transactions (${filteredTxns.length})`}>
        <Table responsive className="billr-table mb-0">
          <thead>
            <tr>
              <th>Txn ID</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Gateway</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTxns.map((txn) => (
              <tr key={txn.id} className={txn.flagged ? "row-flagged" : ""}>
                <td><code>{txn.id}</code></td>
                <td>{txn.user}</td>
                <td>{txn.type}</td>
                <td><strong>{txn.amount}</strong></td>
                <td>{txn.date}</td>
                <td>{txn.gateway}</td>
                <td><StatusBadge status={txn.statusKey} label={txn.status} /></td>
                <td>
                  <div className="d-flex gap-1 flex-wrap">
                    <button className="btn-billr btn-billr-outline btn-billr-sm" disabled={!["success","completed"].includes(txn.statusKey) || actionLoading === `${txn.id}-refund`} onClick={() => runAction(txn.id, "refund")}>Refund</button>
                    <button className="btn-billr btn-billr-outline btn-billr-sm" disabled={txn.statusKey !== "failed" || actionLoading === `${txn.id}-reprocess`} onClick={() => runAction(txn.id, "reprocess")}>Retry</button>
                    <button className="btn-billr btn-billr-primary btn-billr-sm" disabled={actionLoading?.startsWith(txn.id)} onClick={() => runAction(txn.id, txn.flagged ? "unflag" : "flag")}>
                      {txn.flagged ? "Unflag" : "Flag"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </DataCard>
    </div>
  );
};

export default Transactions;
